import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "./Upload.css";
import { UploadHeader } from '../Upload/UploadHeader';
import { usePostHog } from 'posthog-js/react';
import { AiOutlineClose, AiOutlineDown } from 'react-icons/ai';
import { IoChevronUp, IoChevronDown } from 'react-icons/io5';
import { ClipLoader } from "react-spinners";
import { FocusClip } from '../../components/FocusClip/FocusClip';
import { getClip } from '../../services/HalogenService';
import { set } from 'date-fns';
import { useAudio } from '../../contexts/AudioContext';
import { pixelsToVh } from '../../helpers/PixelsToViewport';
import { EXTENDED_HEADER_HEIGHT, MOBILE_EXTENDED_HEADER_HEIGHT, PLAYBACK_BAR_HEIGHT, MOBILE_PLAYBACK_BAR_HEIGHT } from '../../constants';
import { publishText, publishUrl } from '../../services/WaxService';

function Upload() {
    const navigate = useNavigate();
    const posthog = usePostHog();
    const [inputValue, setInputValue] = useState("");
    const [uploadUrl, setUploadUrl] = useState(null);
    const [uploadText, setUploadText] = useState(null);
    const [isAutoAddChecked, setIsAutoAddChecked] = useState(true);
    const [resultClip, setResultClip] = useState(null);
    const [wasError, setWasError] = useState(false);
    const [expandTextarea, setExpandTextarea] = useState(false); // State to control textarea expansion
    const [articleText, setArticleText] = useState(""); // State to store article text
    const { addToQueue } = useAudio();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        if (uploadUrl) {
            publishUrl(uploadUrl, signal).then((data) => {
                posthog.capture('upload_url_succeeded', { value: uploadUrl })
                onCompleted(data['publishedAt']);
            }
            ).catch(error => {
                posthog.capture('upload_url_failed', { value: uploadUrl })
                onError(error);
            });
        }

        return () => {
            abortController.abort();
        };
    }, [uploadUrl]);

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        if (uploadText) {
            publishText(uploadText, signal).then((data) => {
                posthog.capture('upload_text_succeeded', { value: uploadText })
                onCompleted(data['publishedAt']);
            }
            ).catch(error => {
                posthog.capture('upload_text_failed', { value: uploadText })
                onError(error);
            });
        }

        return () => {
            abortController.abort();
        };
    }, [uploadText]);

    const onCompleted = (url) => {
        const clipId = url.substring(url.lastIndexOf('/') + 1);
        getClip(clipId).then((clip) => {
            if (isAutoAddChecked) {
                addToQueue(clip[0]);
            }
            navigate(`/focus/${clipId}`);
        });
    }

    const onError = (error) => {
        setWasError(true);
        setExpandTextarea(true);
        clearInputValue();
    }

    const autoAddChanged = () => {
        setIsAutoAddChecked(!isAutoAddChecked);
    }

    const submitValue = () => {
        posthog.capture('upload_url_performed', { value: inputValue })
        setWasError(false);
        setUploadUrl(inputValue);
    }

    const submitArticleTextValue = () => {
        posthog.capture('upload_text_performed', { value: articleText })
        setWasError(false);
        setUploadText(articleText);
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            submitValue();
        }
    };

    const handleArticleTextKeyDown = (e) => {
        if (e.key === 'Enter') {
            submitArticleTextValue();
        }
    };

    const handleChange = (e) => {
        setInputValue(e.target.value);
    };

    const clearInputValue = () => {
        setUploadUrl(null);
        setUploadText(null);
        setInputValue("");
        setResultClip(null);
    };

    const clearArticleTextValue = () => {
        setUploadUrl(null);
        setUploadText(null);
        setArticleText("");
        setResultClip(null);
    };

    const toggleTextarea = () => {
        setExpandTextarea(!expandTextarea);
    };

    const handleArticleTextChange = (e) => {
        setArticleText(e.target.value);
    };

    return (
        <>
            <UploadHeader />
            <div className="uploadContainer" style={{ height: `${100 - pixelsToVh(windowWidth > 768 ? EXTENDED_HEADER_HEIGHT + PLAYBACK_BAR_HEIGHT : MOBILE_EXTENDED_HEADER_HEIGHT + MOBILE_PLAYBACK_BAR_HEIGHT, window.innerHeight)}dvh` }}>
                <div className="uploadContents">
                    <h1 className="uploadUrlHeader">Give a URL to be summarized into audio.</h1>
                    <div className='columnView'>
                        <div className="uploadBarContainer">
                            <input
                                className="uploadInput"
                                type="text"
                                placeholder="URL"
                                value={inputValue}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className='uploadButtonContainer'>
                            {(!uploadUrl) && (
                                <>
                                    <button className="submitUrlButton" onClick={submitValue}>Submit URL</button>
                                </>
                            )}
                            {(uploadUrl) && (
                                <button className="cancelSubmitUrlButton" onClick={clearInputValue}>Cancel Submission</button>
                            )}
                        </div>
                        {(uploadUrl) &&
                            <div className="submissionLoadingContainer">
                                <ClipLoader color={"#FFFFFF"} loading={(uploadUrl) ? true : false} size={35} />
                                <p className="loadingText">Submission in progress... This may take 1-2 minutes.</p>
                                <div className="autoAddDiv">
                                    <input id="autoAddCheckbox" type={"checkbox"} checked={isAutoAddChecked} onChange={autoAddChanged} />
                                    <label htmlFor="autoAddCheckbox" style={{ marginLeft: '5px', cursor: 'pointer', userSelect: 'none' }}>Auto-add to queue when done?</label>
                                </div>
                            </div>
                        }
                        {wasError && (
                            <div className="submissionErrorContainer" >
                                <p className="uploadUrlError">Sorry, we were unable to generate your audio clip. If you haven't already, consider copying and pasting the article text into the text box instead.</p>
                            </div>
                        )}
                        <div className="articleTextContainer">
                            <div onClick={toggleTextarea} className="expandText">
                                <p className="uploadUrlSubheader">...or give the article as just text</p>
                                {expandTextarea && <IoChevronUp color={'#F2EFF4'} style={{ paddingLeft: '10px' }} />}
                                {!expandTextarea && <IoChevronDown color={'#F2EFF4'} style={{ paddingLeft: '10px' }} />}
                            </div>
                            {expandTextarea && (
                                <>
                                    <div className="uploadBarContainer">
                                        <textarea
                                            className="articleTextarea"
                                            placeholder="Paste article text here"
                                            value={articleText}
                                            onChange={handleArticleTextChange}
                                            onKeyDown={handleArticleTextKeyDown}
                                            rows={10}
                                        />

                                    </div>
                                    <div className='uploadButtonContainer'>
                                        {(!uploadText) && (
                                            <>
                                                <button className="submitTextButton" onClick={submitArticleTextValue}>Submit Text</button>
                                            </>
                                        )}
                                        {(uploadText) && (
                                            <button className="cancelSubmitTextButton" onClick={clearArticleTextValue}>Cancel Submission</button>
                                        )}
                                    </div>
                                    {(uploadText) &&
                                        <div className="submissionLoadingContainer">
                                            <ClipLoader color={"#FFFFFF"} loading={(uploadText) ? true : false} size={35} />
                                            <p className="loadingText">Submission in progress... This may take 1-2 minutes.</p>
                                            <div className="autoAddDiv">
                                                <input id="autoAddCheckbox" type={"checkbox"} checked={isAutoAddChecked} onChange={autoAddChanged} />
                                                <label htmlFor="autoAddCheckbox" style={{ marginLeft: '5px', cursor: 'pointer', userSelect: 'none' }}>Auto-add to queue when done?</label>
                                            </div>
                                        </div>
                                    }
                                </>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

export default Upload;
